import {ChangeDetectionStrategy, Component, Inject, OnInit} from '@angular/core';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {MatTableDataSource} from '@angular/material/table';
import {SegmentsClientService} from '../../segments-client.service';
import {Store} from '@ngrx/store';
import {withLatestFrom} from 'rxjs/operators';
import {selectAccounts} from '../../../target/target.state';
import {reloadSegmentListWithProps} from '../../segments.state';

export interface BulkDeleteSegmentsData {
  segmentIds: number[]
}

export interface SegmentDeletionValidationServerResponse {
  segmentIdToCriticalFailure: { [segmentId: string]: string },
  segmentIdToSharedAccounts: { [segmentId: string]: number[] },
}

interface DeletionValidationModel {
  segmentId: string,
  messages: string[]
}

@Component({
  selector: 'app-bulk-delete-segments-dialog',
  template: `
    <table mat-table [dataSource]="processingDataSource"
           *ngIf="processingDataSource.data; else waiting"
           class="mat-elevation-z0">
      <ng-container matColumnDef="segmentId">
        <th mat-header-cell *matHeaderCellDef style="width: 82px;padding-right: 15px"> Segment ID</th>
        <td mat-cell *matCellDef="let element"> {{ element['segmentId'] }}</td>
      </ng-container>
      <ng-container matColumnDef="message">
        <th mat-header-cell *matHeaderCellDef>Messages</th>
        <td mat-cell *matCellDef="let element">
          <p *ngFor="let item of element['messages']">
            {{ item }}
          </p>
        </td>
      </ng-container>

      <tr mat-header-row *matHeaderRowDef="['segmentId', 'message']"></tr>
      <tr mat-row *matRowDef="let row; columns: ['segmentId', 'message'];"></tr>
    </table>
    <ng-template #waiting>
      Waiting for validation result
      <mat-spinner [mode]="'indeterminate'"
                   [diameter]="25"></mat-spinner>
    </ng-template>
    <div mat-dialog-actions>
      <button (click)="abort()" mat-raised-button type="reset">Abort</button>
      <button type="submit"
              (click)="confirmDelete()"
              [disabled]="this.processingDataSource.data.length <1"
              color="primary"
              id="button-segment-delete-confirm"
              mat-raised-button>Confirm deletion
      </button>
    </div>
  `,
  styleUrls: ['./bulk-delete-segments-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class BulkDeleteSegmentsDialogComponent implements OnInit {
  processingDataSource = new MatTableDataSource<DeletionValidationModel>();
  criticalResults: { [key: string]: string[] } = {};

  constructor(public dialogRef: MatDialogRef<BulkDeleteSegmentsDialogComponent>,
              @Inject(MAT_DIALOG_DATA) public data: BulkDeleteSegmentsData,
              private segmentsClient: SegmentsClientService,
              private state: Store
  ) {
  }

  ngOnInit(): void {
    this.segmentsClient.validateSegmentsDeletion(this.data.segmentIds)
      .pipe(
        withLatestFrom(this.state.select(selectAccounts))
      ).subscribe(([validationResult, accounts]) => {
      this.criticalResults = Object.keys(validationResult.segmentIdToCriticalFailure)
        .reduce((curr: { [key: string]: string[] }, next: string) => {
          return {
            ...curr,
            [next]: ['❌ Critical error: ' + validationResult.segmentIdToCriticalFailure[next]]
          };
        }, {});
      const sharedToAccounts = Object.keys(validationResult.segmentIdToSharedAccounts || {})
        .reduce((curr: { [key: string]: string[] }, next: string) => {
          const accountDescriptions = validationResult.segmentIdToSharedAccounts[next].map(
            accountId => {
              return accounts.filter(acc => acc.id === accountId)[0]?.description;
            }
          );
          return {
            ...curr,
            [next]: ['⚠️Caution, segment is shared with:  ' + accountDescriptions.join(', ')]
          };
        }, {});
      this.processingDataSource.data = this.data.segmentIds.map(segmentId => {
        const messages = [...this.criticalResults[String(segmentId)] || [], ...sharedToAccounts[String(segmentId)]];
        return <DeletionValidationModel>{
          segmentId: String(segmentId),
          messages: messages || ['✅ Safe to delete']
        };
      });
    });
  }

  abort(): void {
    this.dialogRef.close();
  }

  confirmDelete() {
    const segmentIds = this.processingDataSource.data
      .filter(it => {
        return !it.messages.join().includes('Critical');
      })
      .map((it => it.segmentId));
    this.segmentsClient.deleteSegments(segmentIds)
      .subscribe(next => {
        this.state.dispatch(reloadSegmentListWithProps({props: 'clear-selection'}));
        this.dialogRef.close();
      });
  }
}
